<template>
  <v-card class="pa-4">
    <h2>Closed Assignments by Clerk (By Month)</h2>
    <v-row dense>
      <v-col cols="12" md="3">
        <v-select
          v-model="selectedRole"
          :items="roleOptions"
          label="User Role"
          dense
          required
        />
      </v-col>
      <v-col cols="12" md="3">
        <v-select
          v-model="selectedBranch"
          :items="filteredBranches"
          item-text="name"
          item-value="id"
          label="Branch"
          dense
          required
          :disabled="
            selectedRole === 'clerk' ||
            selectedRole === 'manager' ||
            !selectedRole
          "
        />
      </v-col>
      <v-col cols="12" md="3">
        <v-select
          v-model="selectedClerk"
          :items="filteredClerks"
          item-text="username"
          item-value="id"
          label="Clerk"
          dense
          required
          :disabled="
            (selectedRole === 'branchclerk' ||
              selectedRole === 'branchmanager') &&
            !selectedBranch
          "
        />
      </v-col>
      <v-col cols="12" md="3">
        <v-menu v-model="menuMonth" :close-on-content-click="false">
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="selectedMonth"
              label="Month"
              prepend-icon="mdi-calendar"
              readonly
              v-bind="attrs"
              v-on="on"
              dense
              required
            />
          </template>
          <v-date-picker
            v-model="selectedMonth"
            type="month"
            @change="menuMonth = false"
          />
        </v-menu>
      </v-col>
    </v-row>
    <v-divider class="my-4" />
    <v-btn
      color="primary"
      class="mt-2 mr-2"
      :disabled="!selectedClerk || !selectedMonth"
      @click="fetchTable"
      :loading="loading"
    >
      <v-icon left>mdi-table</v-icon>
      Generate Table
    </v-btn>
    <v-btn
      color="success"
      class="mt-2"
      :disabled="!tableData.length"
      @click="exportExcel"
      :loading="exporting"
    >
      <v-icon left>mdi-file-excel</v-icon>
      Export Closed Assignments
    </v-btn>
    <v-alert v-if="noData" type="warning" outlined class="mt-4">
      No closed assignments found for the selected filters.
    </v-alert>
    <v-alert v-else type="info" outlined class="mt-4">
      The exported Excel file will contain closed assignments by clerk for your
      selected role and month.
    </v-alert>
    <div v-if="tableData.length" class="mt-4">
      <div class="mb-2" style="font-weight: bold; font-size: 1.1em">
        Summary of Closed Assignments
      </div>
      <v-simple-table dense>
        <thead>
          <tr>
            <th colspan="2" style="font-size: 1.1em">
              <strong>
                Total Files: {{ tableData.length }}
                for
                {{ clerkName }} - Month: {{ formattedMonth }}
              </strong>
            </th>
          </tr>
        </thead>
        <tbody>
          <!-- Insurer Breakdown -->
          <tr>
            <td colspan="2" style="background: #e3f2fd; font-weight: bold">
              Breakdown by Insurer
            </td>
          </tr>
          <tr v-for="ins in summary.insurerCounts" :key="'ins-' + ins.name">
            <td>
              Insurer: <strong>{{ ins.name }}</strong>
            </td>
            <td>{{ ins.count }}</td>
          </tr>
          <!-- Department Breakdown -->
          <tr>
            <td colspan="2" style="background: #fff3e0; font-weight: bold">
              Breakdown by Department
            </td>
          </tr>
          <tr
            v-for="dept in summary.departmentCounts"
            :key="'dept-' + dept.name"
          >
            <td>
              Department: <strong>{{ dept.name }}</strong>
            </td>
            <td>{{ dept.count }}</td>
          </tr>
          <!-- File Classification Breakdown -->
          <tr>
            <td colspan="2" style="background: #f3e5f5; font-weight: bold">
              Breakdown by File Classification
            </td>
          </tr>
          <tr v-for="fc in summary.fileClassDeptCounts" :key="'fc-' + fc.key">
            <td>
              {{ fc.dept }} - <strong>{{ fc.fileClass }}</strong>
            </td>
            <td>{{ fc.count }}</td>
          </tr>
        </tbody>
      </v-simple-table>
    </div>
  </v-card>
</template>
<script>
import MemberDataService from "../../services/MemberDataService";
import BranchDataService from "../../services/BranchDataService";
import DeptDataService from "../../services/DeptDataService";
import InsDataService from "../../services/InsDataService";
import axios from "axios";
import moment from "moment";
import { apiConfig } from "../../config/api.config";

export default {
  name: "ClosedByClerkMonth",
  data() {
    return {
      roleOptions: [
        { text: "CLERK", value: "clerk" },
        { text: "MANAGER", value: "manager" },
        { text: "BRANCHCLERK", value: "branchclerk" },
        { text: "BRANCHMANAGER", value: "branchmanager" },
      ],
      selectedRole: null,
      branches: [],
      selectedBranch: null,
      clerks: [],
      selectedClerk: null,
      selectedMonth: null,
      menuMonth: false,
      exporting: false,
      loading: false,
      tableData: [],
      noData: false,
      deptMap: {},
      subDeptMap: {},
      insurerMap: {},
      statusMap: {},
      summary: {
        departments: [],
        fileClasses: [],
        insurers: [],
        departmentCounts: [],
        fileClassCounts: [],
        fileClassDeptCounts: [],
        insurerCounts: [],
      },
    };
  },
  computed: {
    filteredBranches() {
      if (!this.selectedRole) return [];
      if (this.selectedRole === "clerk" || this.selectedRole === "manager") {
        // Show "ALL BRANCH" when disabled
        return [{ id: null, name: "ALL BRANCH" }];
      }
      // All branches except HQKL, uppercase and sorted
      return this.branches
        .filter((b) => b.name !== "HQKL")
        .map((b) => ({
          ...b,
          name: (b.name || "").toUpperCase(),
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
    },
    filteredClerks() {
      if (!this.selectedRole) return [];
      // For clerk/manager, filter only by role, ignore branch
      if (this.selectedRole === "clerk" || this.selectedRole === "manager") {
        return this.clerks
          .filter(
            (clerk) =>
              clerk.active &&
              clerk.roles &&
              clerk.roles.some((b) => b.roleCode === this.selectedRole)
          )
          .map((clerk) => ({
            ...clerk,
            username: (clerk.username || "").toUpperCase(),
          }))
          .sort((a, b) => a.username.localeCompare(b.username));
      }
      // For branchclerk/branchmanager, filter by role and branch
      if (!this.selectedBranch) return [];
      const branchObj = this.branches.find((b) => b.id === this.selectedBranch);
      if (!branchObj) return [];
      const branchCode = branchObj.brCode;
      return this.clerks
        .filter(
          (clerk) =>
            clerk.active &&
            clerk.roles &&
            clerk.roles.some((b) => b.roleCode === this.selectedRole) &&
            Array.isArray(clerk.branches) &&
            clerk.branches.some((b) => b.brCode === branchCode)
        )
        .map((clerk) => ({
          ...clerk,
          username: (clerk.username || "").toUpperCase(),
        }))
        .sort((a, b) => a.username.localeCompare(b.username));
    },
    clerkName() {
      const clerk = this.clerks.find((c) => c.id === this.selectedClerk);
      return clerk ? clerk.username : "";
    },
    formattedMonth() {
      if (!this.selectedMonth) return "";
      const d = moment(this.selectedMonth, "YYYY-MM");
      return d.isValid() ? d.format("MM/YYYY") : this.selectedMonth;
    },
  },
  mounted() {
    this.loadBranches();
    this.retrieveClerks();
    this.loadDeptMap();
    this.loadSubDeptMap();
    this.loadInsurerMap();
    this.loadStatusMap();
  },
  watch: {
    selectedRole(newRole) {
      if (newRole === "branchclerk" || newRole === "branchmanager") {
        this.selectedBranch = null;
      }
      this.selectedClerk = null;
    },
    selectedBranch() {
      this.selectedClerk = null;
    },
  },
  methods: {
    async loadBranches() {
      const res = await BranchDataService.getAll();
      this.branches = (res.data || [])
        .map((b) => ({
          ...b,
          name: (b.name || "").toUpperCase(),
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
    },
    retrieveClerks() {
      MemberDataService.getAll()
        .then((response) => {
          // Only filter by active, let computed property filter by role
          this.clerks = response.data
            .filter((item) => item.active)
            .map((item) => ({
              ...item,
              username: (item.username || "").toUpperCase(),
            }))
            .sort((a, b) => a.username.localeCompare(b.username));
        })
        .catch((e) => {
          console.log(e);
        });
    },
    async loadDeptMap() {
      try {
        const res = await DeptDataService.getAll();
        this.deptMap = {};
        (res.data || []).forEach((d) => {
          this.deptMap[d.id] = d.name ? d.name.toUpperCase() : "";
        });
      } catch {
        this.deptMap = {};
      }
    },
    async loadSubDeptMap() {
      try {
        const res = await axios.get(`${apiConfig.baseURL}/subDepts`);
        this.subDeptMap = {};
        (res.data || []).forEach((sd) => {
          this.subDeptMap[sd.id] = sd.subCode ? sd.subCode.toUpperCase() : "";
        });
      } catch {
        this.subDeptMap = {};
      }
    },
    async loadInsurerMap() {
      try {
        const res = await InsDataService.getAll();
        this.insurerMap = {};
        (res.data || []).forEach((i) => {
          this.insurerMap[i.id] = i.name ? i.name.toUpperCase() : "";
        });
      } catch {
        this.insurerMap = {};
      }
    },
    async loadStatusMap() {
      try {
        const res = await axios.get(`${apiConfig.baseURL}/stages`);
        this.statusMap = {};
        (res.data || []).forEach((s) => {
          this.statusMap[s.stageCode] = s.name ? s.name.toUpperCase() : "";
        });
      } catch {
        this.statusMap = {};
      }
    },
    async fetchTable() {
      this.loading = true;
      this.noData = false;
      this.tableData = [];
      this.summary = {
        departments: [],
        fileClasses: [],
        insurers: [],
        departmentCounts: [],
        fileClassCounts: [],
        fileClassDeptCounts: [],
        insurerCounts: [],
      };
      try {
        const params = {
          clerkId: this.selectedClerk,
          month: this.selectedMonth
            ? moment(this.selectedMonth).format("YYYY-MM")
            : "",
        };
        const res = await axios.get(
          `${apiConfig.baseURL}/compliance/closed-by-clerk`,
          { params }
        );
        this.tableData = (res.data || []).map((f) => ({
          id: f.id,
          vehicleNo: (f.vehicleNo || "").toUpperCase(),
          dateOfAssign: f.dateOfAssign
            ? moment(f.dateOfAssign).format("DD/MM/YYYY")
            : "",
          dateFinal: f.dateFinal
            ? moment(f.dateFinal).format("DD/MM/YYYY")
            : "",
          branch: "", // not needed for summary
          department: this.deptMap[f.refType] || "",
          fileClass: this.subDeptMap[f.subRefType] || "",
          status: this.statusMap[f.fileStatus] || f.fileStatus || "",
          insurer: this.insurerMap[f.insurer] || "",
          claimNo: f.claimNo || "",
          aging:
            f.dateOfAssign && f.dateFinal
              ? moment(f.dateFinal).diff(moment(f.dateOfAssign), "days")
              : "",
        }));
        if (!this.tableData.length) {
          this.noData = true;
        } else {
          this.computeSummary();
        }
      } catch {
        this.noData = true;
      } finally {
        this.loading = false;
      }
    },
    computeSummary() {
      const departments = [];
      const fileClasses = [];
      const insurers = [];
      const departmentCounts = {};
      const fileClassCounts = {};
      const insurerCounts = {};
      const fileClassDeptCounts = {};

      this.tableData.forEach((row) => {
        if (row.department) {
          if (!departments.includes(row.department))
            departments.push(row.department);
          departmentCounts[row.department] =
            (departmentCounts[row.department] || 0) + 1;
        }
        if (row.fileClass) {
          if (!fileClasses.includes(row.fileClass))
            fileClasses.push(row.fileClass);
          fileClassCounts[row.fileClass] =
            (fileClassCounts[row.fileClass] || 0) + 1;
        }
        if (row.insurer) {
          if (!insurers.includes(row.insurer)) insurers.push(row.insurer);
          insurerCounts[row.insurer] = (insurerCounts[row.insurer] || 0) + 1;
        }
        // File Classification by Department
        const dept = row.department || "";
        const fileClass = row.fileClass || "";
        if (dept && fileClass) {
          const key = `${dept}|||${fileClass}`;
          fileClassDeptCounts[key] = (fileClassDeptCounts[key] || 0) + 1;
        }
      });

      const fileClassDeptCountsArr = Object.entries(fileClassDeptCounts)
        .map(([key, count]) => {
          const [dept, fileClass] = key.split("|||");
          return { key, dept, fileClass, count };
        })
        .sort((a, b) =>
          a.dept === b.dept
            ? a.fileClass.localeCompare(b.fileClass)
            : a.dept.localeCompare(b.dept)
        );

      this.summary = {
        departments,
        fileClasses,
        insurers,
        departmentCounts: Object.entries(departmentCounts).map(
          ([name, count]) => ({ name, count })
        ),
        fileClassCounts: Object.entries(fileClassCounts).map(
          ([name, count]) => ({ name, count })
        ),
        fileClassDeptCounts: fileClassDeptCountsArr,
        insurerCounts: Object.entries(insurerCounts).map(([name, count]) => ({
          name,
          count,
        })),
      };
    },
    async exportExcel() {
      this.exporting = true;
      try {
        const params = {
          clerkId: this.selectedClerk,
          month: this.selectedMonth
            ? moment(this.selectedMonth).format("YYYY-MM")
            : "",
        };
        const url =
          `${apiConfig.baseURL}/compliance/closed-by-clerk/export?` +
          new URLSearchParams(params).toString();
        window.open(url, "_blank");
      } catch (err) {
        this.$emit("error", err.message || "Failed to export Excel");
      } finally {
        this.exporting = false;
      }
    },
  },
};
</script>
